import React, { useState } from 'react'
import IntlMessages from '@crema/utility/IntlMessages'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import GoBack from 'components/GoBack'
import { RouteConfigComponentProps } from 'react-router-config'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { offeringTagCreateThunk, offeringTagListThunk, selectAvailableTags, selectCurrentTempleOfferingTags, selectOfferingTagStatus, tagListThunk } from 'store/reducers/temple/offeringTagSlice'
import FitContentPaper from 'components/Paper/FitContentPaper'
import FormOfferingTag, { OfferingTagFormValues } from './FormOfferingTag'
import BulkProgressList, { BulkOperationProgress } from './BulkProgressList'
import { FormikHelpers } from 'formik'
import { unwrapResult } from '@reduxjs/toolkit'
import { extractError, getFieldErrors } from 'utils/api'
import { IOfferingTagResponse } from 'types/api/temple'
import useRestricter from 'hooks/useRestricter'
import { PERMISSIONS, RESTRICTED_PATHS } from 'utils/permissions'
import { selectCurrentTempleOfferings } from 'store/reducers/temple/offeringSlice'
import Alert from '@material-ui/lab/Alert'

const AddOfferingTag: React.FC<RouteConfigComponentProps> = (props) => {
    const api = useAppSelector(selectOfferingTagStatus)
    const dispatch = useAppDispatch()
    const [addedItems, setAddedItems] = useState<IOfferingTagResponse[]>([])
    const [submitError, setSubmitError] = useState('')
    const [progress, setProgress] = useState<BulkOperationProgress[] | null>(null)
    const offerings = useAppSelector(selectCurrentTempleOfferings)
    const offeringTags = useAppSelector(selectCurrentTempleOfferingTags)
    const availableTags = useAppSelector(selectAvailableTags)
    const hasAddPermission = useRestricter(PERMISSIONS.ADD, RESTRICTED_PATHS.OFFERING_TAGS)

    const isSameOffering = (tagOffering: string | number, selectedOffering: string | number) => {
        if (tagOffering === selectedOffering || String(tagOffering) === String(selectedOffering)) return true
        const offering = offerings.find(item => item.url === selectedOffering || String(item.id) === String(selectedOffering))
        return Boolean(offering && (tagOffering === offering.url || String(tagOffering) === String(offering.id)))
    }

    const getOfferingName = (offeringRef: string | number) => {
        const found = offerings.find(o => o.url === offeringRef || String(o.id) === String(offeringRef))
        return found ? found.name : String(offeringRef)
    }

    const handleSubmit = async (data: OfferingTagFormValues, actions: FormikHelpers<OfferingTagFormValues>) => {
        setAddedItems([])
        setSubmitError('')
        setProgress(null)

        const selectedOfferings = data.offerings
        const duplicateOfferings = selectedOfferings.filter(offering =>
            offeringTags.some(tagRelation =>
                tagRelation.tag === data.tag &&
                isSameOffering(tagRelation.offering, offering)
            )
        )
        const offeringsToCreate = selectedOfferings.filter(offering => !duplicateOfferings.includes(offering))

        if (offeringsToCreate.length === 0) {
            actions.setFieldError('offerings', 'Selected offerings already have this tag')
            actions.setSubmitting(false)
            return
        }

        // Initialize progress state
        const initialProgress = offeringsToCreate.map(offering => ({
            offeringId: offering,
            offeringName: getOfferingName(offering),
            operation: 'create' as const,
            status: 'pending' as const
        }))
        setProgress(initialProgress)

        try {
            const promises = offeringsToCreate.map(async (offering) => {
                try {
                    const res = await dispatch(offeringTagCreateThunk({ offering, tag: data.tag })).then(unwrapResult)
                    setProgress(prev => prev ? prev.map(item => 
                        item.offeringId === offering ? { ...item, status: 'success' } : item
                    ) : null)
                    return res
                } catch (err: any) {
                    const errMsg = extractError(err) || 'Failed to assign tag'
                    setProgress(prev => prev ? prev.map(item => 
                        item.offeringId === offering ? { ...item, status: 'failed', error: errMsg } : item
                    ) : null)
                    throw err
                }
            })

            const results = await Promise.allSettled(promises)
            const createdItems = results
                .filter((result): result is PromiseFulfilledResult<IOfferingTagResponse> => result.status === 'fulfilled')
                .map(result => result.value)
            const failedItems = results.filter(result => result.status === 'rejected')

            if (createdItems.length > 0) {
                setAddedItems(createdItems)
                dispatch(offeringTagListThunk({}))
            }
            if (failedItems.length > 0) {
                setSubmitError(`${failedItems.length} offering assignment${failedItems.length === 1 ? '' : 's'} failed. See details on the right.`)
            } else if (duplicateOfferings.length > 0) {
                setSubmitError(`${duplicateOfferings.length} selected offering${duplicateOfferings.length === 1 ? '' : 's'} already had this tag and were skipped`)
            } else {
                actions.resetForm()
            }
        } catch (err: any) {
            Object.entries(getFieldErrors(data, err)).forEach(([key, value]) =>
                actions.setFieldError(key, value))
            setSubmitError(extractError(err))
        } finally {
            actions.setSubmitting(false)
        }
    }

    React.useEffect(() => {
        dispatch(offeringTagListThunk({}))
        dispatch(tagListThunk({ tag_type: 'offering' }))
    }, [dispatch])

    const handleSuccess = () => {
        if (addedItems.length === 0) return ''
        const tagName = addedItems[0].tagName ?? addedItems[0].name ?? 'Tag'
        return `Offering Tag ${tagName} assigned to ${addedItems.length} offering${addedItems.length === 1 ? '' : 's'}`
    }

    const initialValues: OfferingTagFormValues = {
        offering: '',
        offerings: [],
        tag: ''
    }

    if (!hasAddPermission) return null

    return (
        <GoBack {...props}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="flex-start">
                <Box style={{ flexGrow: 1, maxWidth: '100%' }}>
                    <FitContentPaper style={{ width: 'min(1180px, 100%)' }}>
                        <Box component="h5" mb={4}><IntlMessages id="offeringTag.add.title" /></Box>
                        <FormOfferingTag
                            buttonLabelId="common.create"
                            api={api}
                            initialValues={initialValues}
                            onSubmitCb={handleSubmit}
                            isSuccess={addedItems.length > 0}
                            successCb={handleSuccess}
                            offerings={offerings}
                            availableTags={availableTags}
                            offeringTags={offeringTags}
                            isBulkAssign
                        />
                        {submitError && (
                            <Box marginTop={4}>
                                <Alert severity={addedItems.length > 0 ? 'warning' : 'error'}>{submitError}</Alert>
                            </Box>
                        )}
                    </FitContentPaper>
                </Box>
                {progress && (
                    <Box 
                        ml={{ xs: 0, md: 4 }} 
                        mt={{ xs: 4, md: 0 }} 
                        style={{ width: '100%', maxWidth: '400px', flexShrink: 0 }}
                    >
                        <BulkProgressList progress={progress} />
                    </Box>
                )}
            </Box>
        </GoBack>
    )
}

export default AddOfferingTag
